import React, { useState, useRef } from 'react';
import { BarChart3, X, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import FileUploadSection from './FileUploadSection';
import { inputClass, labelClass } from './vaultUtils';

const DematEntryModal = ({ mode, entry, onClose, onSave, saving }) => {
  const [formData, setFormData] = useState({
    save_as: entry?.save_as || '',
    demat_account_number: entry?.data?.demat_account_number || '',
    broker_name: entry?.data?.broker_name || '',
    rm_name: entry?.data?.rm_name || '',
    rm_number: entry?.data?.rm_number || '',
    nominee_name: entry?.data?.nominee_name || ''
  });

  const [files, setFiles] = useState(entry?.files || []);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.save_as.trim()) {
      toast.error('Please enter a name to save as');
      return;
    }
    onSave(formData, pendingFiles);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="demat-entry-modal">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-pink-500/10">
              <BarChart3 className="w-5 h-5 text-pink-400" />
            </div>
            <h2 className="text-xl font-bold text-white">
              {mode === 'add' ? 'Add Demat Account' : 'Edit Demat Account'}
            </h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-2 hover:bg-slate-800 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-180px)] space-y-6">
          <FileUploadSection 
            mode={mode} 
            entry={entry} 
            files={files} 
            setFiles={setFiles} 
            pendingFiles={pendingFiles} 
            setPendingFiles={setPendingFiles} 
            uploading={uploading} 
            setUploading={setUploading} 
            fileInputRef={fileInputRef} 
          />

          <div className="space-y-2">
            <Label className={labelClass}>Save As *</Label>
            <Input name="save_as" value={formData.save_as} onChange={handleChange} placeholder="e.g., Zerodha Demat" className={inputClass} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={labelClass}>Demat Account Number</Label>
              <Input name="demat_account_number" value={formData.demat_account_number} onChange={handleChange} placeholder="Account number" className={inputClass} />
            </div>
            <div className="space-y-2">
              <Label className={labelClass}>Broker Name</Label>
              <Input name="broker_name" value={formData.broker_name} onChange={handleChange} placeholder="e.g., Zerodha, ICICI Direct" className={inputClass} />
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-white font-medium border-b border-slate-700 pb-2">Relationship Manager (Optional)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={labelClass}>RM Name</Label>
                <Input name="rm_name" value={formData.rm_name} onChange={handleChange} placeholder="Full name" className={inputClass} />
              </div>
              <div className="space-y-2">
                <Label className={labelClass}>RM Number</Label>
                <Input name="rm_number" value={formData.rm_number} onChange={handleChange} placeholder="Contact number" className={inputClass} />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label className={labelClass}>Nominee Name</Label>
            <Input name="nominee_name" value={formData.nominee_name} onChange={handleChange} placeholder="Full name of nominee" className={inputClass} />
          </div>
        </form>

        <div className="p-6 border-t border-slate-800 flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onClose} className="border-slate-600 text-slate-300 hover:bg-slate-800">Cancel</Button>
          <Button type="submit" onClick={handleSubmit} disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold">
            {saving ? <><Loader2 size={16} className="mr-2 animate-spin" /> Saving...</> : mode === 'add' ? 'Add Entry' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DematEntryModal;
